import { NavigateFunction, useNavigate } from 'react-router-dom';

import AnalyzeService from '@/client/services/api/AnalyzeService';
import { AuthConfig } from '@/client/types/AuthConfig';
import { PartnerLoginConfig } from '@/client/types/preauth/Preauth';
import PreAuthService from '@/client/services/api/PreAuthService';
import { useAuthStore } from '@/client/services/state/authStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { useLocalStorage } from 'usehooks-ts';
import { useMutation } from '@tanstack/react-query';
import { usePartnerStore } from '../../state/partnerStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

interface LoginVariables {
  userid: string;
  password: string;
  loginType: 'employeeId' | 'email';
  partnerLoginConfig?: PartnerLoginConfig;
  purchaseProductId?: string;
  handleError?: () => void;
}

/**
 * Does the stored redirect point at one of the landing routes (`explore` / `dashboard`)?
 *
 * Matches the PATH only. redirectLocation carries a query string, and on the MCP consent
 * round-trip that query holds a client-supplied redirect_uri — so a substring test against the
 * whole value let a third party's callback URL decide where the user lands after login.
 */
export const redirectsToLanding = (redirectLocation: string, landing: string): boolean => {
  let path: string;

  try {
    path = new URL(redirectLocation, window.location.origin).pathname;
  } catch {
    return false;
  }

  return path === `/${landing}` || path.startsWith(`/${landing}/`);
};

// `useLocalStorage` round-trips through JSON; older writes may be raw strings.
const readStoredRedirectLocation = (): string | null => {
  const raw = localStorage.getItem('redirectLocation');

  if (raw === null) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

export const successfulLoginActions = async (
  res: any,
  authConfig: AuthConfig,
  setAuthConfig: (authConfig: AuthConfig) => Promise<void>,
  angularRedirectLocation: string | null,
  redirectLocation: string | null,
  navigate: NavigateFunction,
  purchaseProductId?: string,
) => {
  await setAuthConfig(res);
  AnalyzeService.recordUserAgents(navigator.userAgent);
  AnalyzeService.recordAuthTokens('signin');

  if (purchaseProductId) {
    navigate(`/product/${purchaseProductId}/payment-method`);
    return;
  }

  if (redirectLocation) {
    // reason: consume-once. LoggedInLayout writes this on every unauthenticated
    // render and only logout clears it, so leaving it behind lets a stale value
    // outrank a freshly-set `preRedirect` on the next sign-in. Only clear it when
    // this argument *is* the stored value — HubTransition passes a literal
    // destination and must not consume a redirect it never read.
    if (readStoredRedirectLocation() === redirectLocation) {
      localStorage.removeItem('redirectLocation');
    }

    if (res.company.landing === 'dashboard' && redirectsToLanding(redirectLocation, 'explore')) {
      navigate('/dashboard');
    } else if (
      res.company.landing === 'explore' &&
      redirectsToLanding(redirectLocation, 'dashboard')
    ) {
      navigate('/explore');
    } else {
      navigate(redirectLocation);
    }
  } else if (angularRedirectLocation) {
    window.location.href = `/pvt/#${angularRedirectLocation}`;
  } else if (res.company.landing === 'dashboard') {
    navigate('/dashboard');
  } else {
    navigate('/explore');
  }
};

export const useLogin = () => {
  const { config } = useConfigStore();
  const { setToast } = useToastStore();
  const { t } = useTranslation();
  const { authConfig, setAuthConfig } = useAuthStore();
  const [redirectLocation] = useLocalStorage('redirectLocation', null);
  const [angularRedirectLocation] = useLocalStorage('preRedirect', null);
  const { setPartner, clearPartnerStore } = usePartnerStore();
  const navigate = useNavigate();

  const {
    mutate: login,
    isLoading: loginLoading,
    data,
    error,
  } = useMutation({
    mutationFn: ({ userid, password, loginType, partnerLoginConfig }: LoginVariables) =>
      PreAuthService.login(config.host, password, userid, loginType, partnerLoginConfig),

    onSuccess: async (res, variables) => {
      // Forcibly update the partner store to whatever the valid login state is.
      if (res.user.partner) {
        setPartner(res.user.partner);
      } else {
        clearPartnerStore();
      }
      await successfulLoginActions(
        res,
        authConfig,
        setAuthConfig,
        angularRedirectLocation,
        redirectLocation,
        navigate,
        variables.purchaseProductId,
      );
    },
    onError: (err: any, { loginType, handleError }) => {
      if (handleError) {
        handleError();
        return;
      }

      if (loginType === 'email') {
        if (err && err.status === 401) {
          setToast({
            show: true,
            status: 'error',
            title: t('preauth.signin.login_through_company_im'),
          });
        }
      }

      setToast({
        show: true,
        status: 'error',
        title: t('modules.notifications.invalidCredentials'),
      });
    },
  });

  return { login, loginLoading, data, error };
};
